'use client'

import { Canvas, useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import { Suspense, useState, useCallback, startTransition, useEffect, useRef, useMemo } from 'react'
import { PointsGlobe } from './points-globe'
import { GlobeControls } from './globe-controls'
import {
	BlockInfoPanel,
	type BlockData,
	type SelectedBlockInfo,
	type SelectedOwnedBlockInfo,
	generateBlockData,
	fetchBlockData
} from './block-info-panel'
import BitmapInvoice from './bitmap/bitmap-invoice'
import BitmapListingInvoice, { type SelectedListingInfo } from './bitmap/bitmap-listing-invoice'
import type { BitmapListing, CanvasState } from '@/utils/hooks/useBitmap'
import { ThermalCore } from './thermal-core'
import * as THREE from 'three'
import { Feed } from '@/app/collection/[id]/v2/feed'

// Use React.ElementRef to get the type from the OrbitControls component
type OrbitControlsType = React.ElementRef<typeof OrbitControls>
import useCollection from '@/utils/hooks/useCollection'
import useCollectionStats from '@/utils/hooks/useCollectionStats'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { X, Locate } from 'lucide-react'
import event from '@/utils/event'
import CollectionDetail from '@/components/search-input/pages/collection-detail'

// Calculate grid position from block index
function getGridPosition(index: number): THREE.Vector3 {
	const col = index % 1000
	const row = Math.floor(index / 1000)
	const gridX = (col - 500) * 0.004
	const gridY = (500 - row) * 0.004
	return new THREE.Vector3(gridX, gridY, 0)
}

// Calculate sphere position from block index using Fibonacci sphere
function getSpherePosition(index: number, radius: number = 1): THREE.Vector3 {
	const maxPoints = 1000000
	const phi = (1 + Math.sqrt(5)) / 2
	const angleIncrement = Math.PI * 2 * phi

	const t = index / maxPoints
	const inclination = Math.acos(1 - 2 * t)
	const azimuth = angleIncrement * index

	const x = Math.sin(inclination) * Math.cos(azimuth) * radius
	const y = Math.sin(inclination) * Math.sin(azimuth) * radius
	const z = Math.cos(inclination) * radius

	return new THREE.Vector3(x, y, z)
}

// Component to dynamically adjust pan/rotate speed based on camera distance
function DynamicControlsSpeed({
	controlsRef,
	viewMode
}: {
	controlsRef: React.RefObject<OrbitControlsType | null>
	viewMode: 'globe' | 'grid'
}) {
	const { camera } = useThree()

	useEffect(() => {
		if (!controlsRef.current || viewMode === 'grid') return

		// Update pan speed every frame based on camera distance
		let frameId: number

		const updateSpeed = () => {
			if (controlsRef.current) {
				const distance = camera.position.length()
				// Scale pan speed: at distance 10 use 1.5, at distance 1.1 use very slow
				// This makes panning slower when zoomed in close
				const minDist = 1.1
				const maxDist = 10
				const minSpeed = 0.09
				const maxSpeed = 1.5

				const t = Math.max(0, Math.min(1, (distance - minDist) / (maxDist - minDist)))
				const panSpeed = minSpeed + t * (maxSpeed - minSpeed)

				controlsRef.current.panSpeed = panSpeed
				// Also scale rotate speed
				controlsRef.current.rotateSpeed = 0.18 + t * 0.42
			}
			frameId = requestAnimationFrame(updateSpeed)
		}

		frameId = requestAnimationFrame(updateSpeed)

		return () => {
			cancelAnimationFrame(frameId)
		}
	}, [camera, controlsRef, viewMode])

	return null
}

// Component to control camera for view mode changes and block focus
function CameraController({
	viewMode,
	controlsRef,
	focusBlock,
	onFocusComplete
}: {
	viewMode: 'globe' | 'grid'
	controlsRef: React.RefObject<OrbitControlsType | null>
	focusBlock: number | null
	onFocusComplete?: () => void
}) {
	const { camera } = useThree()
	const prevViewMode = useRef(viewMode)
	const animationRef = useRef<number | null>(null)

	// Handle view mode changes
	useEffect(() => {
		if (viewMode === 'grid' && prevViewMode.current !== 'grid') {
			// Reset camera to look straight at the grid
			camera.position.set(0, 0, 3)
			camera.lookAt(0, 0, 0)
			if (controlsRef.current) {
				controlsRef.current.target.set(0, 0, 0)
				controlsRef.current.update()
			}
		}
		prevViewMode.current = viewMode
	}, [viewMode, camera, controlsRef])

	// Handle focus block animation
	useEffect(() => {
		if (focusBlock === null || focusBlock < 0 || focusBlock >= 1000000) return

		// Cancel any existing animation
		if (animationRef.current) {
			cancelAnimationFrame(animationRef.current)
		}

		const startPosition = camera.position.clone()
		const startTarget = controlsRef.current?.target.clone() || new THREE.Vector3(0, 0, 0)

		let targetPosition: THREE.Vector3
		let targetLookAt: THREE.Vector3

		if (viewMode === 'grid') {
			// Grid mode: zoom into the block position
			targetLookAt = getGridPosition(focusBlock)
			targetPosition = new THREE.Vector3(targetLookAt.x, targetLookAt.y, 0.3) // Zoom close
		} else {
			// Globe mode: position camera to look at the point on sphere
			const pointOnSphere = getSpherePosition(focusBlock)
			// Position camera outside the sphere, looking at the point
			const cameraDistance = 1.5
			targetPosition = pointOnSphere.clone().normalize().multiplyScalar(cameraDistance)
			targetLookAt = new THREE.Vector3(0, 0, 0) // Look at center
		}

		const duration = 1000 // 1 second animation
		const startTime = performance.now()

		const animate = () => {
			const elapsed = performance.now() - startTime
			const progress = Math.min(elapsed / duration, 1)

			// Ease out cubic
			const eased = 1 - Math.pow(1 - progress, 3)

			// Interpolate camera position
			camera.position.lerpVectors(startPosition, targetPosition, eased)

			// Interpolate look target
			if (controlsRef.current) {
				const currentTarget = new THREE.Vector3().lerpVectors(startTarget, targetLookAt, eased)
				controlsRef.current.target.copy(currentTarget)
				controlsRef.current.update()
			}

			if (progress < 1) {
				animationRef.current = requestAnimationFrame(animate)
			} else {
				animationRef.current = null
				onFocusComplete?.()
			}
		}

		animationRef.current = requestAnimationFrame(animate)

		return () => {
			if (animationRef.current) {
				cancelAnimationFrame(animationRef.current)
			}
		}
	}, [focusBlock, viewMode, camera, controlsRef, onFocusComplete])

	return null
}

// Set to true to show globe debug controls
const SHOW_GLOBE_CONTROLS = false

interface GlobeVisualizationProps {
	initialViewMode?: 'globe' | 'grid'
	listedBlocks?: Set<number>
	ownedBlocks?: Set<number>
	ownedInscriptionsMap?: Map<number, any>
	listingsMap?: Map<number, BitmapListing>
	focusBlock?: number | null
	onFocusComplete?: () => void
	currentHeight?: number
	filterMode?: CanvasState
}

export function GlobeVisualization({
	initialViewMode = 'globe',
	listedBlocks,
	ownedBlocks,
	ownedInscriptionsMap,
	listingsMap,
	focusBlock = null,
	onFocusComplete,
	currentHeight = 1000000,
	filterMode = 'show-all'
}: GlobeVisualizationProps) {
	const [pointSize, setPointSize] = useState(initialViewMode === 'grid' ? 1.0 : 1.0)
	const [autoRotate, setAutoRotate] = useState(false)
	const [selectedBlock, setSelectedBlock] = useState<BlockData | null>(null)
	const [selectedIndex, setSelectedIndex] = useState(-1)
	const [selectedBlocks, setSelectedBlocks] = useState<SelectedBlockInfo[]>([])
	const [coreSize, setCoreSize] = useState(0.75)
	const [viewMode, setViewMode] = useState<'globe' | 'grid'>(initialViewMode)
	const controlsRef = useRef<OrbitControlsType | null>(null)

	// Internal focus block state for relocate functionality
	const [internalFocusBlock, setInternalFocusBlock] = useState<number | null>(null)

	// Combine external focusBlock with internal for camera control
	const activeFocusBlock = focusBlock ?? internalFocusBlock

	// Cart state for purchases
	const [cartItems, setCartItems] = useState<string[]>([])

	// Invoice state (for purchases)
	const [showInvoice, setShowInvoice] = useState(false)

	// Listing state (for selling owned blocks)
	const [selectedOwnedBlocks, setSelectedOwnedBlocks] = useState<SelectedOwnedBlockInfo[]>([])
	const [listingPrices, setListingPrices] = useState<Record<number, number>>({})
	const [globalListingPrice, setGlobalListingPrice] = useState<number | null>(null)
	const [showListingInvoice, setShowListingInvoice] = useState(false)

	// Fetch collection data for Feed
	const { data: meta } = useCollection('bitmap')
	const { data: stats } = useCollectionStats('bitmap')

	// Fetch exchange rate data for thermal core visualization
	const { exchangeRate, percentChange24h } = useExchangeRate()

	// Build a Set of block numbers with listings under $20
	const lowPriceBlocks = useMemo(() => {
		const set = new Set<number>()
		if (!listingsMap || !exchangeRate) return set

		const LOW_PRICE_THRESHOLD_USD = 20

		listingsMap.forEach((listing, blockNum) => {
			const usdPrice = (listing.satoshiPrice / 1e8) * exchangeRate
			if (usdPrice < LOW_PRICE_THRESHOLD_USD) {
				set.add(blockNum)
			}
		})

		return set
	}, [listingsMap, exchangeRate])

	// Add item to cart
	const handleAddToCart = useCallback((inscriptionId: string) => {
		setCartItems((prev) => {
			if (prev.includes(inscriptionId)) {
				return prev // Already in cart
			}
			return [...prev, inscriptionId]
		})
	}, [])

	// Clear cart
	const handleClearCart = useCallback(() => {
		setCartItems([])
	}, [])

	// Track previous view mode for detecting changes
	const prevViewModeRef = useRef(initialViewMode)

	// Sync viewMode with prop changes and auto-relocate to selected block
	useEffect(() => {
		const viewModeChanged = prevViewModeRef.current !== initialViewMode
		prevViewModeRef.current = initialViewMode

		const frameId = requestAnimationFrame(() => {
			setViewMode(initialViewMode)
			// Update point size when view mode changes
			setPointSize(initialViewMode === 'grid' ? 1.0 : 1.0)
		})

		// Auto-relocate to selected block when switching view modes
		let timeoutId: ReturnType<typeof setTimeout> | undefined
		if (viewModeChanged && selectedIndex >= 0) {
			// Small delay to let view mode update first
			timeoutId = setTimeout(() => {
				setInternalFocusBlock(selectedIndex)
			}, 50)
		}
		return () => {
			cancelAnimationFrame(frameId)
			if (timeoutId !== undefined) clearTimeout(timeoutId)
		}
	}, [initialViewMode, selectedIndex])

	// Select block when focusing
	useEffect(() => {
		if (focusBlock !== null && focusBlock >= 0 && focusBlock < currentHeight) {
			startTransition(() => {
				setSelectedIndex(focusBlock)
				setSelectedBlock(generateBlockData(focusBlock))
			})
			// Fetch real data from API
			fetchBlockData(focusBlock).then((data) => {
				startTransition(() => setSelectedBlock(data))
			})
		}
	}, [focusBlock, currentHeight])

	// Clear selections when switching to "show-all" mode
	// This prevents getting stuck on "my lands" blocks when going back to "all"
	useEffect(() => {
		if (filterMode === 'show-all') {
			requestAnimationFrame(() => {
				setSelectedBlock(null)
				setSelectedIndex(-1)
				setSelectedBlocks([])
				setSelectedOwnedBlocks([])
				setListingPrices({})
				setGlobalListingPrice(null)
			})
		}
	}, [filterMode])

	const [currentTier, setCurrentTier] = useState(1)
	const [cameraDistance, setCameraDistance] = useState(3.0)

	const handlePointClick = useCallback(
		(index: number, shiftKey?: boolean) => {
			// Don't allow clicking on unmined blocks
			if (index >= currentHeight) {
				console.log(`[Click] Block ${index} not yet mined (height: ${currentHeight})`)
				return
			}

			const listing = listingsMap?.get(index)
			const isOwned = ownedBlocks?.has(index)
			const inscription = ownedInscriptionsMap?.get(index)
			console.log(
				`handlePointClick called with index: ${index}, shiftKey: ${shiftKey}, forSale: ${!!listing}, isOwned: ${isOwned}`
			)

			// If the block is for sale (and not owned by us), toggle it in the cart-style selection
			if (listing && !isOwned) {
				// Clear any listing selection when buying
				setSelectedOwnedBlocks([])
				setListingPrices({})
				setGlobalListingPrice(null)

				// Check if already in selection
				const isAlreadySelected = selectedBlocks.some((sb) => sb.block.index === index)

				if (isAlreadySelected) {
					// Remove from selection
					const remaining = selectedBlocks.filter((sb) => sb.block.index !== index)
					setSelectedBlocks(remaining)
					// Update highlight to last remaining item or -1
					if (remaining.length > 0) {
						setSelectedIndex(remaining[remaining.length - 1].block.index)
					} else {
						setSelectedIndex(-1)
					}
				} else {
					// Add to selection
					const newBlock = generateBlockData(index)
					const newSelection: SelectedBlockInfo = { block: newBlock, listing }
					setSelectedBlocks((prev) => [...prev, newSelection])
					// Highlight the newly added block
					setSelectedIndex(index)
				}

				// Clear single block info panel when shopping
				setSelectedBlock(null)
				return
			}

			// If the block is owned by user, toggle it in the listing selection
			if (isOwned && inscription) {
				// Clear any purchase selection when listing
				setSelectedBlocks([])

				// Check if already in listing selection
				const isAlreadySelected = selectedOwnedBlocks.some((sb) => sb.block.index === index)

				if (isAlreadySelected) {
					// Remove from selection
					const remaining = selectedOwnedBlocks.filter((sb) => sb.block.index !== index)
					setSelectedOwnedBlocks(remaining)
					// Remove price for this block
					setListingPrices((prev) => {
						const newPrices = { ...prev }
						delete newPrices[index]
						return newPrices
					})
					// Update highlight to last remaining item or -1
					if (remaining.length > 0) {
						setSelectedIndex(remaining[remaining.length - 1].block.index)
					} else {
						setSelectedIndex(-1)
					}
				} else {
					// Add to selection
					const newBlock = generateBlockData(index)
					const newSelection: SelectedOwnedBlockInfo = { block: newBlock, inscription }
					setSelectedOwnedBlocks((prev) => [...prev, newSelection])
					// Highlight the newly added block
					setSelectedIndex(index)
				}

				// Clear single block info panel when listing
				setSelectedBlock(null)
				return
			}

			// For non-listed, non-owned blocks, show the info panel
			setSelectedBlocks([])
			setSelectedOwnedBlocks([])
			setListingPrices({})
			setGlobalListingPrice(null)

			startTransition(() => {
				setSelectedIndex(index)
				setSelectedBlock(generateBlockData(index))
			})

			// Fetch real data from API
			fetchBlockData(index).then((data) => {
				startTransition(() => setSelectedBlock(data))
			})
		},
		[
			currentHeight,
			listingsMap,
			selectedBlocks,
			ownedBlocks,
			ownedInscriptionsMap,
			selectedOwnedBlocks
		]
	)

	const handleLodChange = useCallback((tier: number, distance: number) => {
		setCurrentTier(tier)
		setCameraDistance(distance)
	}, [])

	// Relocate to selected block
	const handleRelocate = useCallback(() => {
		if (selectedIndex >= 0) {
			setInternalFocusBlock(selectedIndex)
		}
	}, [selectedIndex])

	// Remove block from multi-selection
	const handleRemoveBlock = useCallback((blockIndex: number) => {
		setSelectedBlocks((prev) => prev.filter((sb) => sb.block.index !== blockIndex))
	}, [])

	// Buy multiple blocks - opens the invoice
	const handleBuyMultiple = useCallback(() => {
		const listedBlocks = selectedBlocks.filter((sb) => sb.listing)
		if (listedBlocks.length === 0) return
		setShowInvoice(true)
	}, [selectedBlocks])

	// Handle invoice completion
	const handleInvoiceComplete = useCallback(() => {
		setShowInvoice(false)
		setSelectedBlocks([])
		setSelectedBlock(null)
		setSelectedIndex(-1)
	}, [])

	// Close invoice
	const handleInvoiceClose = useCallback(() => {
		setShowInvoice(false)
	}, [])

	// Buy single block - opens the invoice
	const handleBuySingle = useCallback((blockHeight: number, listing: BitmapListing) => {
		// Create a single-item selection for the invoice
		const singleSelection: SelectedBlockInfo = {
			block: generateBlockData(blockHeight),
			listing
		}
		setSelectedBlocks([singleSelection])
		setShowInvoice(true)
	}, [])

	// ===== LISTING HANDLERS =====

	// Remove owned block from listing selection
	const handleRemoveOwnedBlock = useCallback((blockIndex: number) => {
		setSelectedOwnedBlocks((prev) => prev.filter((sb) => sb.block.index !== blockIndex))
		setListingPrices((prev) => {
			const newPrices = { ...prev }
			delete newPrices[blockIndex]
			return newPrices
		})
	}, [])

	// Handle price change for a specific block
	const handlePriceChange = useCallback((blockIndex: number, price: number) => {
		setListingPrices((prev) => ({ ...prev, [blockIndex]: price }))
	}, [])

	// Handle global listing price change
	const handleGlobalListingPriceChange = useCallback((price: number | null) => {
		setGlobalListingPrice(price)
	}, [])

	// List multiple owned blocks - opens the listing invoice
	const handleListMultiple = useCallback(() => {
		if (selectedOwnedBlocks.length === 0) return
		setShowListingInvoice(true)
	}, [selectedOwnedBlocks])

	// Delist multiple owned blocks that are currently listed
	const handleDelistMultiple = useCallback(() => {
		// Get the listed blocks from selectedOwnedBlocks
		const listedOwnedBlocks = selectedOwnedBlocks.filter((sb) => listingsMap?.has(sb.block.index))
		if (listedOwnedBlocks.length === 0) return

		// Open the command tool with bitmap collection and delist tab
		event.emit('command', {
			command: {
				featured: false,
				component: CollectionDetail,
				name: 'Bitmap',
				args: {
					slug: 'bitmap',
					tab: 'delist'
				},
				heading: 'Collections'
			}
		})

		// Clear selection after opening
		setSelectedOwnedBlocks([])
		setSelectedBlock(null)
		setSelectedIndex(-1)
	}, [selectedOwnedBlocks, listingsMap])

	// Handle listing invoice completion
	const handleListingInvoiceComplete = useCallback(() => {
		setShowListingInvoice(false)
		setSelectedOwnedBlocks([])
		setListingPrices({})
		setGlobalListingPrice(null)
		setSelectedBlock(null)
		setSelectedIndex(-1)
	}, [])

	// Close listing invoice
	const handleListingInvoiceClose = useCallback(() => {
		setShowListingInvoice(false)
	}, [])

	// Clear internal focus when animation completes
	const handleInternalFocusComplete = useCallback(() => {
		setInternalFocusBlock(null)
		onFocusComplete?.()
	}, [onFocusComplete])

	const showCart = cartItems.length > 0

	// Prepare selected items for purchase invoice
	const invoiceItems = selectedBlocks.flatMap((sb) =>
		sb.listing
			? [
					{
						blockHeight: sb.block.blockHeight,
						listing: sb.listing!
					}
			  ]
			: []
	)

	// Prepare selected items for listing invoice
	const listingInvoiceItems: SelectedListingInfo[] = selectedOwnedBlocks.map((sb) => ({
		blockHeight: sb.block.blockHeight,
		inscription: sb.inscription,
		priceBTC: listingPrices[sb.block.index] ?? globalListingPrice ?? 0
	}))

	// Check if currently selected single block is owned
	const isCurrentBlockOwned = selectedBlock ? ownedBlocks?.has(selectedBlock.index) : false

	return (
		<div className="relative size-full">
			<BlockInfoPanel
				selectedBlock={selectedBlock}
				selectedBlocks={selectedBlocks}
				listing={selectedBlock ? listingsMap?.get(selectedBlock.index) : undefined}
				listingsMap={listingsMap}
				onAddToCart={handleAddToCart}
				onRemoveBlock={handleRemoveBlock}
				onBuyMultiple={handleBuyMultiple}
				onBuySingle={handleBuySingle}
				onClose={() => {
					setSelectedBlock(null)
					setSelectedIndex(-1)
					setSelectedBlocks([])
					setSelectedOwnedBlocks([])
					setListingPrices({})
					setGlobalListingPrice(null)
				}}
				// Listing mode props
				selectedOwnedBlocks={selectedOwnedBlocks}
				onRemoveOwnedBlock={handleRemoveOwnedBlock}
				onListMultiple={handleListMultiple}
				onDelistMultiple={handleDelistMultiple}
				listingPrices={listingPrices}
				onPriceChange={handlePriceChange}
				globalListingPrice={globalListingPrice}
				onGlobalListingPriceChange={handleGlobalListingPriceChange}
				floorPrice={stats?.floor_price}
				isOwnedBlock={isCurrentBlockOwned}
			/>
			{/* Bitmap Purchase Invoice */}
			{showInvoice && invoiceItems.length > 0 && (
				<BitmapInvoice
					selected={invoiceItems}
					onComplete={handleInvoiceComplete}
					onClose={handleInvoiceClose}
				/>
			)}
			{/* Bitmap Listing Invoice */}
			{showListingInvoice && listingInvoiceItems.length > 0 && (
				<BitmapListingInvoice
					selected={listingInvoiceItems}
					onComplete={handleListingInvoiceComplete}
					onClose={handleListingInvoiceClose}
				/>
			)}
			{/* Relocate button - shows when a block is selected, positioned above the footer */}
			{selectedIndex >= 0 && (
				<Button
					variant="ghost"
					size="icon"
					onClick={handleRelocate}
					className="absolute bottom-28 right-4 z-10 bg-background/70 hover:bg-background/90 text-foreground border border-border"
					title="Relocate to selected block"
				>
					<Locate className="size-5" />
				</Button>
			)}
			{/* Show GlobeControls when enabled and cart is not showing */}
			{SHOW_GLOBE_CONTROLS && !showCart && (
				<GlobeControls
					pointSize={pointSize}
					autoRotate={autoRotate}
					coreSize={coreSize}
					currentTier={currentTier}
					cameraDistance={cameraDistance}
					viewMode={viewMode}
					onPointSizeChange={setPointSize}
					onAutoRotateChange={setAutoRotate}
					onCoreSizeChange={setCoreSize}
					onViewModeChange={(mode) => {
						setViewMode(mode)
						setPointSize(mode === 'grid' ? 1.0 : 1.0)
					}}
				/>
			)}
			{/* Cart/Feed panel */}
			{showCart && (
				<Card className="absolute top-4 right-4 w-[400px] bg-background/95 backdrop-blur-sm border-border z-10 flex flex-col">
					<CardHeader className="pb-2 flex-shrink-0 flex flex-row items-center justify-between">
						<CardTitle className="text-sm font-medium text-foreground">
							Cart ({cartItems.length} item{cartItems.length !== 1 ? 's' : ''})
						</CardTitle>
						<Button
							variant="ghost"
							size="sm"
							onClick={handleClearCart}
							className="size-6 p-0 text-muted-foreground hover:text-foreground"
						>
							<X className="size-4" />
						</Button>
					</CardHeader>
					<CardContent className="p-0 overflow-hidden">
						<Feed
							height="auto"
							title="Bitmap"
							items={[]}
							slug="bitmap"
							supply={meta?.total_supply || 0}
							listed={stats?.listed || 0}
							meta={meta || {}}
							isMobile={false}
							isFungible={false}
							isRune={false}
							onSelectBulk={() => {}}
							bulkSelected={{}}
							cartItems={cartItems}
							minimal={true}
						/>
					</CardContent>
				</Card>
			)}
			<Canvas
				camera={{ position: [0, 0, 3], fov: 60 }}
				gl={{ antialias: false, powerPreference: 'high-performance' }}
				dpr={1}
				frameloop="always"
			>
				<color attach="background" args={['#000000']} />
				<CameraController
					viewMode={viewMode}
					controlsRef={controlsRef}
					focusBlock={activeFocusBlock}
					onFocusComplete={handleInternalFocusComplete}
				/>
				<DynamicControlsSpeed controlsRef={controlsRef} viewMode={viewMode} />
				<Suspense fallback={null}>
					{coreSize > 0 && viewMode === 'globe' && (
						<ThermalCore size={coreSize} percentChange24h={percentChange24h} price={exchangeRate} />
					)}
					<PointsGlobe
						pointSize={pointSize}
						autoRotate={autoRotate}
						selectedIndex={selectedIndex}
						selectedIndices={[
							...selectedBlocks.map((sb) => sb.block.index),
							...selectedOwnedBlocks.map((sb) => sb.block.index)
						]}
						viewMode={viewMode}
						listedBlocks={listedBlocks}
						ownedBlocks={ownedBlocks}
						lowPriceBlocks={lowPriceBlocks}
						filterMode={filterMode}
						onPointClick={handlePointClick}
						onLodChange={handleLodChange}
						maxHeight={currentHeight}
					/>
				</Suspense>
				<OrbitControls
					ref={controlsRef}
					key={viewMode}
					enableDamping={false}
					enableRotate={viewMode !== 'grid'}
					enablePan={true}
					panSpeed={1.5}
					rotateSpeed={0.5}
					zoomSpeed={0.8}
					minDistance={viewMode === 'grid' ? 0.1 : 1.1}
					maxDistance={10}
					screenSpacePanning={true}
					mouseButtons={{
						LEFT: viewMode === 'grid' ? THREE.MOUSE.PAN : THREE.MOUSE.ROTATE,
						MIDDLE: THREE.MOUSE.DOLLY,
						RIGHT: viewMode === 'grid' ? THREE.MOUSE.ROTATE : THREE.MOUSE.PAN
					}}
				/>
			</Canvas>
		</div>
	)
}
